/**
 * AlgoSupplyBreakdown — V2.K.5
 *
 * Renders a supply breakdown for ALGO on the AlgorandMonitor page.
 *
 * Why Algorand is its own story (different from every other chain
 * we cover) :
 *  - **Fixed supply, no inflation, no burn** : the entire 10 B ALGO
 *    was minted at genesis in June 2019. Monetary policy is purely
 *    distributional.
 *  - The supply story is therefore one of **distribution progress** :
 *    how much of the 10 B has entered circulation vs how much the
 *    Foundation still holds. Full distribution is expected by 2030
 *    (extended from the initial 2024 plan).
 *  - The relevant "locked" figure is the ~2 B ALGO currently online-
 *    staked (per algorand.co 2025 roadmap), not a treasury vesting
 *    escrow — Algorand has no equivalent of Cronos CIP-006.
 *  - **Governance Rewards Program ended at GP14 in Q1 2025** after
 *    distributing 687.5 M ALGO, replaced by the Staking Rewards
 *    Program (Algorand 4.0 upgrade, January 2025).
 *  - **xGov mainnet** launched October 2025 — retroactive grants for
 *    builders (council elected July 2025).
 */

import { useTranslation } from 'react-i18next';
import { useAlgoSupply } from '../../hooks/useAlgoSupply';

// Total ALGO distributed across the lifetime of the Governance
// Rewards program (Q4 2021 → Q1 2025 / GP14).
const GOVERNANCE_REWARDS_LIFETIME_TOTAL = 687_500_000;

const KNOWN_ENDPOINTS: Array<{
  label: string;
  url: string;
  category: 'docs' | 'blog' | 'governance';
  note: string;
}> = [
  {
    label: 'Algorand — The Algo (tokenomics officielle)',
    url: 'https://algorand.co/technology/the-algo',
    category: 'docs',
    note: '10 Md ALGO mintés au genesis, distribution prévue jusqu\'en 2030.',
  },
  {
    label: 'Governance Rewards : It\'s a wrap (GP14 — Q1 2025)',
    url: 'https://algorand.co/blog/governance-rewards-its-a-wrap-reflecting-and-what-comes-next',
    category: 'blog',
    note: 'Fin du Governance Rewards Program. Transition vers Staking Rewards.',
  },
  {
    label: '2025 on Algorand : Roadmap progress',
    url: 'https://algorand.co/blog/2025-on-algorand-roadmap-progress',
    category: 'blog',
    note: 'Online stake doublé (~1 Md → ~2 Md ALGO en un an).',
  },
  {
    label: 'Algorand Governance Platform',
    url: 'https://governance.algorand.foundation/',
    category: 'governance',
    note: 'Plateforme gouvernance + xGov (retroactive grants, mainnet oct. 2025).',
  },
];

export function AlgoSupplyBreakdown() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useAlgoSupply();

  const locale = i18n?.language || 'fr';
  const fmt = (n: number) =>
    n.toLocaleString(locale === 'fr' ? 'fr-FR' : locale, { maximumFractionDigits: 0 });
  const fmtB = (n: number) =>
    `${(n / 1_000_000_000).toLocaleString(locale === 'fr' ? 'fr-FR' : locale, {
      maximumFractionDigits: 2,
    })} Md`;

  return (
    <section
      aria-labelledby="algo-supply-breakdown-title"
      style={{
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <header style={{ marginBottom: '14px' }}>
        <h2
          id="algo-supply-breakdown-title"
          style={{
            fontSize: '16px',
            fontWeight: 700,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span aria-hidden style={{ color: '#000000', background: 'white', borderRadius: '50%', width: '18px', height: '18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 900 }}>A</span>
          {t('algo.supply.title', 'Supply ALGO — répartition')}
        </h2>
        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
          {t(
            'algo.supply.subtitle',
            '10 Md ALGO mintés au genesis (juin 2019). Pas d\'inflation ni de burn — la supply progresse uniquement par distribution Foundation jusqu\'en 2030.',
          )}
        </div>
      </header>

      {isLoading ? (
        <SkeletonBlock />
      ) : error || !data ? (
        <ErrorBlock />
      ) : (
        <>
          <StatGrid data={data} fmt={fmt} fmtB={fmtB} t={t} />
          <StackedBar data={data} t={t} />
          <Legend t={t} />
          <DistributionNote
            t={t}
            fmt={fmt}
            data={data}
            governanceLifetime={GOVERNANCE_REWARDS_LIFETIME_TOTAL}
          />
          <KnownEndpoints t={t} />
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stat grid
// ─────────────────────────────────────────────────────────────────────

interface StatGridProps {
  data: NonNullable<ReturnType<typeof useAlgoSupply>['data']>;
  fmt: (n: number) => string;
  fmtB: (n: number) => string;
  t: (key: string, fallback: string) => string;
}

function StatGrid({ data, fmt, fmtB, t }: StatGridProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        marginBottom: '16px',
      }}
    >
      <StatCard
        label={t('algo.supply.circulating', 'En circulation')}
        value={fmtB(data.circulating)}
        sub={`${fmt(data.circulating)} ALGO · ${data.distributedPct.toFixed(1)} %`}
        color="#10b981"
      />
      <StatCard
        label={t('algo.supply.onlineStaked', 'Online stake (référence)')}
        value={fmtB(data.onlineStakeReference)}
        sub={`~${data.onlineStakePctOfCirculating.toFixed(0)} % du circulating`}
        color="#6366f1"
      />
      <StatCard
        label={t('algo.supply.remaining', 'Reste à distribuer')}
        value={fmtB(data.remainingToDistribute)}
        sub={t('algo.supply.remainingNote', 'Détenu par la Foundation — cible 2030')}
        color="#f59e0b"
      />
      <StatCard
        label={t('algo.supply.max', 'Cap maximal (genesis 2019)')}
        value={fmtB(data.max)}
        sub={t('algo.supply.maxNote', 'Fixe et immuable')}
        color="#000000"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  // Special handling for pure black to keep card readable on dark bg
  const bg = color === '#000000' ? 'rgba(255, 255, 255, 0.04)' : `${color}10`;
  const border = color === '#000000' ? 'rgba(255, 255, 255, 0.2)' : `${color}30`;
  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '20px',
          fontWeight: 700,
          color: 'var(--text)',
          marginTop: '4px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          marginTop: '2px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {sub}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stacked bar : online-staked / free-float / remaining-to-distribute
// ─────────────────────────────────────────────────────────────────────

function StackedBar({
  data,
  t,
}: {
  data: NonNullable<ReturnType<typeof useAlgoSupply>['data']>;
  t: (key: string, fallback: string) => string;
}) {
  // Express everything as a % of the 10 B max cap so the bar always
  // reads "where are we on the distribution path".
  const onlineStakedPct = Math.min(100, (data.onlineStakeReference / data.max) * 100);
  const freeFloatPct = Math.max(
    0,
    ((data.circulating - data.onlineStakeReference) / data.max) * 100,
  );
  const remainingPct = Math.max(0, 100 - onlineStakedPct - freeFloatPct);

  return (
    <div
      style={{ marginBottom: '8px' }}
      aria-label={t(
        'algo.supply.barAlt',
        'Répartition de la supply ALGO sur le cap fixe de 10 milliards : online-staked, free float, reste à distribuer',
      )}
      role="img"
    >
      <div
        style={{
          display: 'flex',
          height: '24px',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            width: `${onlineStakedPct}%`,
            background: 'linear-gradient(90deg, #4f46e5, #6366f1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '11px',
            fontWeight: 700,
            transition: 'width 0.5s ease',
          }}
          title={`${onlineStakedPct.toFixed(1)} %`}
        >
          {onlineStakedPct > 8 ? `${onlineStakedPct.toFixed(0)} %` : ''}
        </div>
        <div
          style={{
            width: `${freeFloatPct}%`,
            background: 'linear-gradient(90deg, #10b981, #34d399)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '11px',
            fontWeight: 700,
            transition: 'width 0.5s ease',
          }}
          title={`${freeFloatPct.toFixed(1)} %`}
        >
          {freeFloatPct > 8 ? `${freeFloatPct.toFixed(0)} %` : ''}
        </div>
        <div
          style={{
            width: `${remainingPct}%`,
            background:
              'repeating-linear-gradient(45deg, rgba(255,255,255,0.05), rgba(255,255,255,0.05) 6px, rgba(255,255,255,0.10) 6px, rgba(255,255,255,0.10) 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--muted)',
            fontSize: '10px',
            fontWeight: 600,
            transition: 'width 0.5s ease',
          }}
          title={`${remainingPct.toFixed(1)} %`}
        >
          {remainingPct > 8 ? `${remainingPct.toFixed(1)} %` : ''}
        </div>
      </div>
    </div>
  );
}

function Legend({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '14px',
        flexWrap: 'wrap',
        fontSize: '11px',
        color: 'var(--muted)',
        marginBottom: '14px',
      }}
    >
      <LegendItem color="#6366f1" label={t('algo.supply.legendStaked', 'Online stake (~2 Md ALGO)')} />
      <LegendItem color="#10b981" label={t('algo.supply.legendFloat', 'Free float (circulating non-staked)')} />
      <LegendItem
        color="rgba(255,255,255,0.15)"
        label={t('algo.supply.legendRemaining', 'Foundation — reste à distribuer (cible 2030)')}
      />
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span
        aria-hidden
        style={{
          width: '10px',
          height: '10px',
          borderRadius: '3px',
          background: color,
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Distribution callout
// ─────────────────────────────────────────────────────────────────────

function DistributionNote({
  t,
  fmt,
  data,
  governanceLifetime,
}: {
  t: (key: string, fallback: string) => string;
  fmt: (n: number) => string;
  data: NonNullable<ReturnType<typeof useAlgoSupply>['data']>;
  governanceLifetime: number;
}) {
  const body = t(
    'algo.supply.distributionBody',
    "Distribution actuelle : {{distributed}} % des 10 Md mintés en 2019 sont en circulation, soit {{remaining}} ALGO encore détenus par la Fondation (cible : > 99 % d'ici 2030). Le Governance Rewards Program s'est achevé à GP14 en Q1 2025 après avoir distribué {{governance}} ALGO sur sa durée de vie. Depuis Algorand 4.0 (janvier 2025), les rewards passent par le Staking Rewards Program (30 000 ALGO min/node, block proposer rewards, bonus −1 % par millionième bloc). xGov est passé en mainnet en octobre 2025 pour les retroactive grants.",
  )
    .replace('{{distributed}}', data.distributedPct.toFixed(2))
    .replace('{{remaining}}', fmt(data.remainingToDistribute))
    .replace('{{governance}}', fmt(governanceLifetime));

  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'rgba(99, 102, 241, 0.06)',
        border: '1px dashed rgba(99, 102, 241, 0.35)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--text)',
        lineHeight: 1.6,
        marginBottom: '14px',
      }}
    >
      <strong style={{ color: '#6366f1' }}>
        {t('algo.supply.distributionTitle', 'Distribution Foundation — Staking Rewards depuis 2025')}
      </strong>
      <div style={{ marginTop: '4px' }}>{body}</div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '6px' }}>
        {t(
          'algo.supply.distributionSource',
          'Sources : algorand.co/technology/the-algo, algorand.co/blog (GP14 wrap, 2025 roadmap), governance.algorand.foundation.',
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Known endpoints
// ─────────────────────────────────────────────────────────────────────

function KnownEndpoints({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <details
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <summary
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text)',
          cursor: 'pointer',
          listStyle: 'revert',
        }}
      >
        {t('algo.supply.knownEndpointsTitle', 'Sources officielles & gouvernance')}
      </summary>

      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.6 }}>
        {t(
          'algo.supply.knownEndpointsDisclaimer',
          'Algorand n\'a pas de burn dans sa politique monétaire et pas d\'adresse de burn standard. L\'adresse exacte du treasury de la Fondation n\'est pas listée ici — les distributions Foundation sont visibles sur la gouvernance officielle et via les rapports trimestriels.',
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
        {KNOWN_ENDPOINTS.map((entry) => (
          <li
            key={entry.url}
            style={{
              padding: '10px 0',
              borderTop: '1px solid var(--border)',
              fontSize: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <CategoryPill category={entry.category} />
              <a
                href={entry.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--text)', fontWeight: 600, textDecoration: 'none' }}
              >
                {entry.label} ↗
              </a>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
              {entry.note}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}

function CategoryPill({ category }: { category: 'docs' | 'blog' | 'governance' }) {
  const styles: Record<typeof category, { bg: string; color: string; label: string }> = {
    docs: { bg: 'rgba(255, 255, 255, 0.10)', color: '#e5e7eb', label: 'Docs' },
    blog: { bg: 'rgba(99, 102, 241, 0.15)', color: '#818cf8', label: 'Blog' },
    governance: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', label: 'Gouvernance' },
  };
  const s = styles[category];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: '4px',
        background: s.bg,
        color: s.color,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Loading / error states
// ─────────────────────────────────────────────────────────────────────

function SkeletonBlock() {
  return (
    <div aria-hidden>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              background: 'rgba(255, 255, 255, 0.04)',
              borderRadius: '10px',
              height: '72px',
            }}
          />
        ))}
      </div>
      <div
        style={{
          height: '24px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '8px',
          marginBottom: '14px',
        }}
      />
    </div>
  );
}

function ErrorBlock() {
  return (
    <div
      style={{
        padding: '14px',
        background: 'rgba(239, 68, 68, 0.08)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--muted)',
      }}
    >
      Données CoinGecko indisponibles pour le moment. La supply ALGO sera affichée dès que l'API répondra.
    </div>
  );
}
